import type { Difficulty, EnemyKind, LaneSprintState, PlayerState, WorldState } from './types';
import { DIRECTIONS, manhattan, stepCell, type Cell, type Direction, type EnemyState } from './types';

const BASE_MOVE_TICKS: Readonly<Record<EnemyKind, number>> = {
  tracer: 20,
  rammer: 29,
  seer: 24,
  wisp: 32,
  scoop: 22,
};

/**
 * Cherry Scoop lane charge.
 *
 * The Scoop patrols at `BASE_MOVE_TICKS.scoop`. The moment a cat stands in one
 * of its four lanes with nothing in between, it stops, telegraphs, then runs at
 * `SCOOP_SPRINT_MOVE_TICKS` for eight seconds. A player crosses a tile in six
 * fixed steps on Standard, so a charging Scoop stays outrun-able — it closes
 * ground fast enough to be frightening without being unfair. Afterwards it is
 * winded: it moves at its normal speed again and cannot lock on until the
 * cooldown expires.
 */
export const SCOOP_SIGHT_RANGE = 8;
export const SCOOP_LOCK_TICKS = 34;
export const SCOOP_SPRINT_TICKS = 480;
export const SCOOP_WINDED_TICKS = 180;
export const SCOOP_SPRINT_MOVE_TICKS = 10;

/** Kinds that own a lane-charge cycle get their state at spawn; others get null. */
export function createLaneSprint(kind: EnemyKind): LaneSprintState | null {
  if (kind !== 'scoop') return null;
  return { phase: 'ready', ticksRemaining: 0, lane: null };
}

export function enemyMoveTicks(
  enemy: EnemyState,
  state: WorldState,
  difficulty: Difficulty,
  extraction: boolean,
  isRailCell: (cell: Cell) => boolean,
): number {
  let ticks = BASE_MOVE_TICKS[enemy.kind];
  if (enemy.kind === 'wisp' && state === 'overdrive' && (isRailCell(enemy.cell) || isRailCell(enemy.target))) ticks = 7;
  if (enemy.sprint?.phase === 'sprinting') ticks = SCOOP_SPRINT_MOVE_TICKS;
  if (extraction) ticks *= 0.84;
  if (difficulty === 'relaxed') ticks *= 1.25;
  if (difficulty === 'expert') ticks *= 0.82;
  return Math.max(6, Math.round(ticks));
}

export function nearestPlayer(enemy: EnemyState, players: readonly PlayerState[]): PlayerState | null {
  const living = players.filter((player) => !player.downed);
  living.sort((a, b) => manhattan(enemy.cell, a.cell) - manhattan(enemy.cell, b.cell) || a.id - b.id);
  return living[0] ?? null;
}

export function predictIntercept(player: PlayerState, isBlocked: (cell: Cell) => boolean): Cell {
  let result: Cell = player.target;
  for (let step = 0; step < 2; step += 1) {
    const next = stepCell(result, player.facing);
    if (isBlocked(next)) break;
    result = next;
  }
  return result;
}

/**
 * Direction of an unobstructed straight line from `origin` to `target` within
 * `range` tiles, or null when the target shares no clear lane. Walls, ice, and
 * Frostwalls all break sight, so cutting the lane is a real defence.
 */
export function laneSight(
  origin: Cell,
  target: Cell,
  isBlocked: (cell: Cell) => boolean,
  range = SCOOP_SIGHT_RANGE,
): Direction | null {
  if (origin.x !== target.x && origin.y !== target.y) return null;
  if (origin.x === target.x && origin.y === target.y) return null;
  const direction: Direction = origin.x === target.x
    ? (target.y > origin.y ? 'down' : 'up')
    : (target.x > origin.x ? 'right' : 'left');
  let cursor: Cell = origin;
  for (let step = 1; step <= range; step += 1) {
    cursor = stepCell(cursor, direction);
    if (cursor.x === target.x && cursor.y === target.y) return direction;
    if (isBlocked(cursor)) return null;
  }
  return null;
}

/** The lane a Scoop can see any of the given cats down, nearest lane first. */
export function firstLaneSighting(
  origin: Cell,
  targets: readonly Cell[],
  isBlocked: (cell: Cell) => boolean,
  range = SCOOP_SIGHT_RANGE,
): Direction | null {
  let best: { direction: Direction; distance: number } | null = null;
  for (const target of targets) {
    const direction = laneSight(origin, target, isBlocked, range);
    if (!direction) continue;
    const distance = manhattan(origin, target);
    if (!best || distance < best.distance || (distance === best.distance && DIRECTIONS.indexOf(direction) < DIRECTIONS.indexOf(best.direction))) {
      best = { direction, distance };
    }
  }
  return best?.direction ?? null;
}

/** Runs every fixed step, including while the enemy is mid-tile or stunned. */
export function tickLaneSprint(sprint: LaneSprintState): void {
  if (sprint.ticksRemaining > 0) sprint.ticksRemaining -= 1;
}

export function beginLaneLock(sprint: LaneSprintState, lane: Direction): void {
  sprint.phase = 'locking';
  sprint.ticksRemaining = SCOOP_LOCK_TICKS;
  sprint.lane = lane;
}

export function beginLaneSprint(sprint: LaneSprintState): void {
  sprint.phase = 'sprinting';
  sprint.ticksRemaining = SCOOP_SPRINT_TICKS;
}

/** Ends a charge early or on schedule and starts the breathless cooldown. */
export function windLaneSprint(sprint: LaneSprintState): void {
  sprint.phase = 'winded';
  sprint.ticksRemaining = SCOOP_WINDED_TICKS;
  sprint.lane = null;
}

export function readyLaneSprint(sprint: LaneSprintState): void {
  sprint.phase = 'ready';
  sprint.ticksRemaining = 0;
  sprint.lane = null;
}

/** Normalised 0..1 progress through the current phase, for presentation. */
export function laneSprintPhaseProgress(sprint: LaneSprintState): number {
  const duration = sprint.phase === 'locking'
    ? SCOOP_LOCK_TICKS
    : sprint.phase === 'sprinting'
      ? SCOOP_SPRINT_TICKS
      : sprint.phase === 'winded'
        ? SCOOP_WINDED_TICKS
        : 0;
  if (duration <= 0) return 0;
  return Math.min(1, Math.max(0, 1 - sprint.ticksRemaining / duration));
}
